import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articleLinks } from '@/mocks/trustSignals';

interface Props {
  heroImage: string;
  badge: string;
  title: string;
  date: string;
  children: ReactNode;
}

export default function ArticleLayout({ heroImage, badge, title, date, children }: Props) {
  return (
    <>
      <section className="relative w-full h-[420px] md:h-[500px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-white/20 text-white/80 text-xs font-semibold uppercase tracking-wide mb-4">
                {badge}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight max-w-3xl">
                {title}
              </h1>
            </div>
            <div className="text-white/60 text-sm md:text-right whitespace-nowrap">
              <p>Published: {date}</p>
              <p>Author: Mulebuy Editorial Team</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <article className="flex-1 max-w-3xl">
              <div className="prose-custom">
                {children}
                <div className="mt-12 p-5 bg-secondary-50 rounded-xl border border-secondary-200">
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Explore more resources:{' '}
                    <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 underline">
                      Mulebuy Spreadsheets Home
                    </Link>
                    {' '}&bull;{' '}
                    <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-primary-600 font-semibold hover:text-primary-700 underline">
                      Main Website
                    </a>
                  </p>
                </div>
              </div>
            </article>

            <aside className="lg:w-80 flex-shrink-0">
              <div className="bg-primary-600 rounded-2xl p-6 sticky top-24">
                <h3 className="font-heading text-lg font-bold text-white mb-4">SEO Guides</h3>
                <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto">
                  {articleLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-3 py-2 rounded-lg bg-white/15 text-white text-xs hover:bg-white/25 transition-colors leading-snug"
                    >
                      {link.title}
                    </Link>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-white/20">
                  <p className="text-white/60 text-xs mb-2">Browse Categories</p>
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-2 py-1.5 rounded-md text-white/70 text-xs hover:bg-white/15 transition-colors truncate"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-white/20">
                  <Link
                    to="/"
                    className="block w-full px-4 py-2.5 rounded-lg bg-white text-primary-700 text-sm font-semibold text-center hover:bg-white/90 transition-colors"
                  >
                    <i className="ri-home-4-line mr-1.5"></i>
                    Back to Home
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="px-4 md:px-6 max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
            Explore All Mulebuy Resources
          </h2>
          <p className="text-foreground-500 text-lg mt-4">
            Browse the complete collection of guides, templates, and strategies for spreadsheet-based sourcing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-600 text-white font-semibold text-base hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Home Page
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-accent-500 text-white font-semibold text-base hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              Buy It Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
}